<template>
    <div class="print-container">
        <button @click="printGoals" class="print-btn" :disabled="isLoading">
            <i class="material-icons">print</i> Print Goals
        </button>
        
        <div v-if="isLoading" class="loading-overlay">
            <div class="loading-content">
                <i class="material-icons spinning">autorenew</i>
                <span>Generating your report...</span>
            </div>
        </div>
    </div>
</template>

<script>
import { userService } from '../services/api';
import { goalService } from '../services/goalService';
import { generateGoalsPDF } from '@/services/pdfService';
import { ref, onMounted } from 'vue';

export default {
    name: 'PrintGoals',
    props: {
        userId: {
            type: String,
            required: true,
            validator: (value) => {
                return typeof value === 'string' || value === null;
            }
        },
        year: {
            type: Number,
            required: true
        }
    },
    emits: ['error'],
    setup() {
        const goalTypes = ref([]);
        const isLoading = ref(false);
        
        const loadGoalTypes = async () => {
            try {
                goalTypes.value = await goalService.getAllGoalTypes();
            } catch (error) {
                console.error('Error loading goal types:', error);
            }
        };
        
        onMounted(() => {
            loadGoalTypes();
        });
        
        return {
            goalTypes,
            isLoading
        };
    },
    methods: {
        async printGoals() {
            if (this.isLoading) return;
            
            this.isLoading = true;
            try {
                if (!this.userId) {
                    throw new Error('User ID is required');
                }
                const userProfile = await userService.getProfile(this.userId);
                if (!userProfile) {
                    throw new Error('User profile not found');
                }

                const goals = await goalService.getMemberGoals(this.userId, this.year);
                const goalsWithUpdates = await Promise.all(goals.map(async (goal) => {
                    const updates = await goalService.getGoalUpdates(goal.id);
                    updates.sort((a, b) => new Date(b.update_date) - new Date(a.update_date));
                    return {
                        ...goal,
                        updates,
                        percentage: updates.length > 0 
                            ? Math.round((updates[0].current_value / goal.target_value) * 100)
                            : 0
                    };
                }));

                await generateGoalsPDF(goalsWithUpdates, userProfile, this.year, this.goalTypes);
            } catch (error) {
                console.error('Error generating PDF:', error);
                this.$emit('error', 'Failed to generate PDF. Please try again.');
            } finally {
                this.isLoading = false;
            }
        }
    }
};
</script>

<style scoped>
.print-container {
    position: relative;
    display: inline-block;
}

.print-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    height: 36px;
    padding: 0 1.25rem;
    background-color: #2196F3;
    color: white;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    font-size: 0.9rem;
    font-weight: 500;
    min-width: 120px;
    transition: background-color 0.2s;
    line-height: 1;
}

.print-btn:hover {
    background-color: #1976D2;
}

.print-btn:disabled {
    background-color: #90CAF9;
    cursor: not-allowed;
}

.print-btn i {
    font-size: 18px;
    line-height: 1;
}

.loading-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
}

.loading-content {
    background: white;
    padding: 1.5rem 2rem;
    border-radius: 8px;
    display: flex;
    align-items: center;
    gap: 1rem;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.loading-content i {
    font-size: 24px;
    color: #2196F3;
}

.loading-content span {
    color: #2c3e50;
    font-size: 1rem;
}

@keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
}

.spinning {
    animation: spin 1.5s linear infinite;
}
</style>